import React, { useState } from "react";
import { reportsService, commentsService, upvotesService } from "@/legacy/base44Client";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FileText,
  MessageSquare,
  TrendingUp,
  MapPin,
  Calendar,
  User,
  Activity
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

const categoryIcons = {
  pothole: "🕳️",
  blocked_drain: "🚰",
  waste_collection: "🗑️",
  street_lighting: "💡",
  flooding: "🌊",
  vandalism: "⚠️",
  illegal_dumping: "🚮",
  broken_infrastructure: "🔧",
  water_leak: "💧",
  other: "📍"
};

export default function MyActivity() {
  const navigate = useNavigate();
  
  // Use the new AuthContext hook instead of Base44
  const { user } = useAuth();

  const { data: myReports = [] } = useQuery({
    queryKey: ['userReports', user?.id],
    queryFn: () => reportsService.filter({ created_by: user?.id }, '-created_date'),
    enabled: !!user,
    initialData: [],
  });

  const { data: myComments = [] } = useQuery({
    queryKey: ['userComments', user?.id],
    queryFn: () => commentsService.filter({ user_email: user?.email }, '-created_date'),
    enabled: !!user,
    initialData: [],
  });

  const { data: myUpvotes = [] } = useQuery({
    queryKey: ['userUpvotes', user?.id],
    queryFn: () => upvotesService.filter({ user_id: user?.id }, '-created_date'),
    enabled: !!user,
    initialData: [],
  });

  const { data: allReports = [] } = useQuery({
    queryKey: ['allReportsActivity'],
    queryFn: () => reportsService.list(),
    initialData: [],
  });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F0] dark:bg-gray-900">
        <Card className="p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-4">Please Login</h2>
          <Button onClick={() => navigate('/Login')}>
            Login to View Activity
          </Button>
        </Card>
      </div>
    );
  }

  const upvotedReports = allReports.filter(report => 
    myUpvotes.some(upvote => upvote.report_id === report.id)
  );

  const commentedReports = allReports.filter(report =>
    myComments.some(comment => comment.report_id === report.id)
  );

  return (
    <div className="min-h-screen p-4 md:p-8 bg-[#F0F0F0] dark:bg-gray-900">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Activity className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Activity</h1>
          </div>
          <p className="text-gray-600 dark:text-slate-400">Track all your contributions to the community</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/30 dark:to-slate-800 border-blue-200 dark:border-blue-700">
            <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{myReports.length}</p>
            <p className="text-sm text-gray-600 dark:text-slate-400">Reports</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/30 dark:to-slate-800 border-purple-200 dark:border-purple-700">
            <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{myComments.length}</p>
            <p className="text-sm text-gray-600 dark:text-slate-400">Comments</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/30 dark:to-slate-800 border-amber-200 dark:border-amber-700">
            <TrendingUp className="w-6 h-6 text-amber-600 dark:text-amber-400 mb-2" />
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{myUpvotes.length}</p>
            <p className="text-sm text-gray-600 dark:text-slate-400">Upvotes</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-green-50 to-white dark:from-green-900/30 dark:to-slate-800 border-green-200 dark:border-green-700">
            <Activity className="w-6 h-6 text-green-600 dark:text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-700 dark:text-green-400">
              {myReports.length + myComments.length + myUpvotes.length}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400">Total Actions</p>
          </Card>
        </div>

        {/* Activity Tabs */}
        <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700">
          <Tabs defaultValue="reports" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="reports">
                My Reports ({myReports.length})
              </TabsTrigger>
              <TabsTrigger value="comments">
                Comments ({myComments.length})
              </TabsTrigger>
              <TabsTrigger value="upvotes">
                Upvoted ({upvotedReports.length})
              </TabsTrigger>
            </TabsList>

            {/* Reports Tab */}
            <TabsContent value="reports">
              {myReports.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Reports Yet</h3>
                  <p className="text-gray-600 dark:text-slate-400 mb-6">Start contributing by reporting an issue</p>
                  <Button
                    onClick={() => navigate(createPageUrl("ReportIssue"))}
                    className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                  >
                    Report an Issue
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myReports.map((report) => (
                    <motion.div
                      key={report.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ x: 4 }}
                    >
                      <Card
                        className="p-4 cursor-pointer hover:shadow-lg transition-all border-emerald-100 dark:border-emerald-800 dark:bg-slate-800/50 dark:hover:bg-slate-700/50"
                        onClick={() => navigate(createPageUrl(`ReportDetails?id=${report.id}&from=MyActivity`))}
                      >
                        <div className="flex items-start gap-4">
                          <span className="text-3xl">{categoryIcons[report.category]}</span>
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <h3 className="font-bold text-gray-900 dark:text-white">{report.title}</h3>
                              <Badge className={
                                report.status === 'resolved' 
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                  : report.status === 'in_progress'
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                              }>
                                {report.status.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-slate-400 mb-3 line-clamp-2">
                              {report.description}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-500">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {report.neighborhood}
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(report.created_date), 'MMM d, yyyy')}
                              </div>
                              <div className="flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" />
                                {report.upvote_count || 0} upvotes
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Comments Tab */}
            <TabsContent value="comments">
              {myComments.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Comments Yet</h3>
                  <p className="text-gray-600 dark:text-slate-400">Engage with the community by commenting on reports</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {myComments.map((comment) => {
                    const report = allReports.find(r => r.id === comment.report_id);
                    return (
                      <motion.div
                        key={comment.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <Card className="p-4 hover:shadow-lg transition-all dark:bg-slate-800/50 dark:border-slate-700">
                          <div className="flex items-start gap-3 mb-3">
                            <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-700 dark:text-slate-300 mb-2">{comment.text}</p>
                              <div className="text-xs text-gray-500 dark:text-slate-500">
                                {format(new Date(comment.created_date), 'MMM d, yyyy h:mm a')}
                              </div>
                            </div>
                          </div>
                          {report && (
                            <div
                              className="mt-3 pt-3 border-t dark:border-slate-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 -m-4 p-4 rounded-b-lg transition-colors"
                              onClick={() => navigate(createPageUrl(`ReportDetails?id=${report.id}&from=MyActivity`))}
                            >
                              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                On: {report.title}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-500">
                                <span>{categoryIcons[report.category]}</span>
                                <span>{report.neighborhood}</span>
                              </div>
                            </div>
                          )}
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Upvotes Tab */}
            <TabsContent value="upvotes">
              {upvotedReports.length === 0 ? (
                <div className="text-center py-12">
                  <TrendingUp className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Upvotes Yet</h3>
                  <p className="text-gray-600 dark:text-slate-400">Support important issues by upvoting them</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upvotedReports.map((report) => (
                    <motion.div
                      key={report.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ x: 4 }}
                    >
                      <Card
                        className="p-4 cursor-pointer hover:shadow-lg transition-all dark:bg-slate-800/50 dark:border-slate-700 dark:hover:bg-slate-700/50"
                        onClick={() => navigate(createPageUrl(`ReportDetails?id=${report.id}&from=MyActivity`))}
                      >
                        <div className="flex items-start gap-4">
                          <span className="text-3xl">{categoryIcons[report.category]}</span>
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <h3 className="font-bold text-gray-900 dark:text-white">{report.title}</h3>
                              <Badge className={
                                report.status === 'resolved' 
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                              }>
                                {report.status.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-slate-400 mb-3 line-clamp-2">
                              {report.description}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-500">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {report.neighborhood}
                              </div>
                              <div className="flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" />
                                {report.upvote_count || 0} upvotes
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}